import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

import { DateTools, Icon, IconButton, TIconName, TTableRow } from '@treezor/design-system';

import usFlag from '@/assets/flags/us.png';
import { TUserRowType, allUsersHeadersList } from '@/features/user/store/allUsers.model';
import { IUser, TPhysicalUserRole } from '@/features/user//store/user.model';
import { UserStatus } from '@/features/user/components/status/UserStatus';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';
import { KycStatus } from '@/features/kyc/components/kyc-infos/KycStatus';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { ALL_USER__PER_PAGE } from '@/features/user/constants';

interface IUsersRows {
  users?: IUser[];
  isLoading?: boolean;
}

export function AllUsersRows({ users = [], isLoading = false }: IUsersRows) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onNavigateToUser = useCallback(
    (userId: number) => {
      userId && navigate(DashboardHelpers.generateProfilePath(userId));
    },
    [navigate],
  );

  const userTypeIcon: Record<TPhysicalUserRole | number, { userType: string; userIcon: TIconName }> = useMemo(
    () => ({
      1: {
        userType: t('pages.dashboard.users.userTypes.1'),
        userIcon: 'person',
      },
      2: {
        userType: t('pages.dashboard.users.userTypes.2'),
        userIcon: 'domain',
      },
      3: {
        userType: t('pages.dashboard.users.userTypes.3'),
        userIcon: 'real_estate_agent',
      },
      4: {
        userType: t('pages.dashboard.users.userTypes.3'),
        userIcon: 'things_to_do',
      },
      legalRepresentative: {
        userType: t('pages.dashboard.users.physicalUserRole.legalRepresentative'),
        userIcon: 'domain',
      },
      shareholder: {
        userType: t('pages.dashboard.users.physicalUserRole.shareholder'),
        userIcon: 'domain',
      },
      legalRepresentativeAndShareholder: {
        userType: t('pages.dashboard.users.physicalUserRole.legalRepresentativeAndShareholder'),
        userIcon: 'domain',
      },
      employee: {
        userType: t('pages.dashboard.users.physicalUserRole.employee'),
        userIcon: 'domain',
      },
      selfEmployed: {
        userType: t('pages.dashboard.users.physicalUserRole.selfEmployed'),
        userIcon: 'work',
      },
      minor: {
        userType: t('pages.dashboard.users.physicalUserRole.minor'),
        userIcon: 'person',
      },
      anonymous: {
        userType: t('pages.dashboard.users.physicalUserRole.anonymous'),
        userIcon: 'person',
      },
      child: {
        userType: t('pages.dashboard.users.physicalUserRole.child'),
        userIcon: 'supervisor_account',
      },
    }),
    [t],
  );

  if (isLoading) {
    return RowSkeleton<TUserRowType>({ headers: allUsersHeadersList, length: ALL_USER__PER_PAGE });
  }

  return users?.map((user): TTableRow<TUserRowType> => {
    const userType = DashboardHelpers.getUserRole(user) ?? user.userTypeId;
    const name = DashboardHelpers.getUserName(user);
    return {
      user: (
        <div className="flex flex-col gap-1 justify-start cursor-pointer" data-testid="user">
          <div className="flex gap-2 items-center">
            {name && (
              <span
                className="typo-body-semibold-small hover:text-interactive"
                onClick={() => onNavigateToUser(user.userId)}
                data-testid="user-link"
              >
                {name}
              </span>
            )}
            {user.specifiedUSPerson === 1 && <img src={usFlag} className="w-[13px] h-[10px] rounded-[2px]" />}
          </div>
          <span
            className={clsx('typo-body-regular-caption', !name && 'cursor-pointer hover:text-interactive')}
            onClick={() => !name && onNavigateToUser(user.userId)}
          >
            ID #{user.userId}
          </span>
        </div>
      ),
      email: <span className="typo-body-regular-small">{user.email}</span>,
      userTypeId: (
        <div className="flex gap-2 items-center whitespace-nowrap">
          <Icon name={userTypeIcon[userType]?.userIcon} />
          <span className="typo-body-regular-caption">{userTypeIcon[userType]?.userType}</span>
        </div>
      ),
      userTag: <span className="typo-body-regular-small">{user.userTag}</span>,
      parentUserId: user.parentUserId ? (
        <div className="flex items-center" data-testid="parentUserId">
          <span className="typo-body-regular-caption mr-2 text-tertiary">#{user.parentUserId}</span>
          <IconButton
            icon="chevron_right"
            variant="secondary"
            onClick={() => onNavigateToUser(user.parentUserId)}
            testId="parentUserId-link"
          />
        </div>
      ) : (
        <></>
      ),
      userStatus: DashboardHelpers.getUserStatus(user) && <UserStatus {...DashboardHelpers.getUserStatus(user)} />,
      kycStatus: <KycStatus user={user} />,
      createdDate: <span className="typo-body-regular-small">{DateTools.format(user.createdDate)}</span>,
      actions: <></>,
      onClick: () => onNavigateToUser(user.userId),
    };
  });
}
